(() => {
  const menuSelector = '.codex-header-nav__details';
  const finePointerHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  let headerScrollController;

  const initDirectionAwareHeader = () => {
    headerScrollController?.destroy();
    headerScrollController = undefined;

    const section = document.querySelector('.section-header');
    if (!section) return;

    const position = window.getComputedStyle(section).position;
    const isSticky = position === 'sticky' || position === '-webkit-sticky';
    if (!isSticky) {
      section.classList.remove('section-header--scroll-ready', 'section-header--scroll-hidden');
      return;
    }

    const movementThreshold = 10;
    let lastScrollY = Math.max(0, window.scrollY);
    let direction = '';
    let distanceInDirection = 0;
    let animationFrame;

    const setHidden = (hidden) => {
      section.classList.toggle('section-header--scroll-hidden', hidden);
    };

    const reveal = () => {
      direction = '';
      distanceInDirection = 0;
      setHidden(false);
    };

    const shouldStayVisible = () => (
      window.Shopify?.designMode ||
      section.classList.contains('section-header--nav-open') ||
      Boolean(section.querySelector(':focus-visible')) ||
      Boolean(section.querySelector(`${menuSelector}[open]`)) ||
      document.body.classList.contains('reveal-mobile-nav') ||
      document.body.classList.contains('reveal-mobile-nav--revealed') ||
      document.body.classList.contains('codex-search-open')
    );

    const topRevealBoundary = () => {
      let boundary = 0;
      let sibling = section.previousElementSibling;
      while (sibling) {
        const siblingStyle = window.getComputedStyle(sibling);
        if (
          siblingStyle.display !== 'none' &&
          siblingStyle.position !== 'absolute' &&
          siblingStyle.position !== 'fixed'
        ) {
          boundary = Math.max(boundary, sibling.offsetTop + sibling.offsetHeight);
        }
        sibling = sibling.previousElementSibling;
      }
      return boundary + 8;
    };

    const update = () => {
      animationFrame = undefined;
      const currentScrollY = Math.max(0, window.scrollY);
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;

      if (currentScrollY <= topRevealBoundary() || shouldStayVisible()) {
        reveal();
        return;
      }

      if (Math.abs(delta) < 0.5) return;

      const nextDirection = delta > 0 ? 'down' : 'up';
      if (nextDirection !== direction) {
        direction = nextDirection;
        distanceInDirection = 0;
      }

      distanceInDirection += Math.abs(delta);
      if (distanceInDirection < movementThreshold) return;

      setHidden(nextDirection === 'down');
      distanceInDirection = 0;
    };

    const requestUpdate = () => {
      if (animationFrame !== undefined) return;
      animationFrame = window.requestAnimationFrame(update);
    };

    const resetAfterResize = () => {
      lastScrollY = Math.max(0, window.scrollY);
      reveal();
    };

    const revealForFocus = () => reveal();

    section.classList.add('section-header--scroll-ready');
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', resetAfterResize);
    window.addEventListener('pageshow', resetAfterResize);
    section.addEventListener('focusin', revealForFocus);

    headerScrollController = {
      reveal,
      destroy: () => {
        if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', resetAfterResize);
        window.removeEventListener('pageshow', resetAfterResize);
        section.removeEventListener('focusin', revealForFocus);
        section.classList.remove('section-header--scroll-ready', 'section-header--scroll-hidden');
      }
    };
  };

  const bindHeaderMenus = (root = document) => {
    root.querySelectorAll(menuSelector).forEach((details) => {
      if (details.dataset.codexBound === 'true') return;
      details.dataset.codexBound = 'true';
      const summary = details.querySelector(':scope > summary');
      let closeTimer;
      let suppressNextFocusOpen = false;

      const cancelClose = () => window.clearTimeout(closeTimer);
      const openForHover = () => {
        if (!finePointerHover.matches) return;
        cancelClose();
        details.open = true;
      };
      const closeAfterHover = () => {
        if (!finePointerHover.matches) return;
        cancelClose();
        closeTimer = window.setTimeout(() => {
          if (!details.matches(':hover') && !details.contains(document.activeElement)) {
            details.open = false;
          }
        }, 120);
      };

      details.addEventListener('mouseenter', openForHover);
      details.addEventListener('mouseleave', closeAfterHover);
      details.addEventListener('focusin', () => {
        cancelClose();
        if (suppressNextFocusOpen) {
          suppressNextFocusOpen = false;
          return;
        }
        details.open = true;
      });
      details.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!details.contains(document.activeElement) && !details.matches(':hover')) {
            details.open = false;
          }
        }, 0);
      });
      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        details.open = false;
        suppressNextFocusOpen = true;
        summary?.focus();
      });
      summary?.addEventListener('click', (event) => {
        if (event.detail > 0 && finePointerHover.matches && details.matches(':hover')) {
          event.preventDefault();
          details.open = true;
        }
      });

      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll(menuSelector).forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const initPredictiveSearch = (root = document) => {
    const overlay = root.querySelector?.('[data-codex-search-overlay]') ||
      (root.matches?.('[data-codex-search-overlay]') ? root : null);
    if (!overlay || overlay.dataset.codexBound === 'true') return;
    overlay.dataset.codexBound = 'true';

    const input = overlay.querySelector('[data-codex-search-input]');
    const form = overlay.querySelector('[data-codex-search-form]');
    const clearButton = overlay.querySelector('[data-codex-search-clear]');
    const defaultContent = overlay.querySelector('[data-codex-search-default]');
    const resultsContainer = overlay.querySelector('[data-codex-search-results]');
    const emptyContent = overlay.querySelector('[data-codex-search-empty]');
    const status = overlay.querySelector('[data-codex-search-status]');
    const allResults = overlay.querySelector('[data-codex-search-all]');
    const queryLabel = overlay.querySelector('[data-codex-search-query]');
    const predictiveUrl = overlay.dataset.predictiveUrl || '/search/suggest';
    const currency = overlay.dataset.currency || 'AUD';
    let activeRequest;
    let debounceTimer;
    let returnFocusTo;

    const setStatus = (message) => {
      status.textContent = message;
    };

    const formatPrice = (value) => {
      const amount = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(amount)) return String(value);

      try {
        const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
        return `${formatted} ${currency}`;
      } catch (_error) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    };

    const resetResults = () => {
      activeRequest?.abort();
      activeRequest = undefined;
      window.clearTimeout(debounceTimer);
      resultsContainer.replaceChildren();
      resultsContainer.hidden = true;
      emptyContent.hidden = true;
      defaultContent.hidden = false;
      allResults.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      overlay.classList.remove('codex-search-overlay--loading');
      setStatus('');
    };

    const resultLink = (item, type, query) => {
      const link = createElement(type === 'query' ? 'button' : 'a', `codex-predictive-result codex-predictive-result--${type}`);
      link.setAttribute('role', 'option');
      if (type === 'query') {
        link.type = 'button';
        link.addEventListener('click', () => {
          input.value = item.text || item.title || query;
          form.requestSubmit();
        });
      } else {
        const resultUrl = item.url || form.action;
        try {
          const sanitizedUrl = new URL(resultUrl, window.location.origin);
          ['_psq', '_psid', '_pos', '_ss'].forEach((parameter) => sanitizedUrl.searchParams.delete(parameter));
          link.href = sanitizedUrl.toString();
        } catch (_error) {
          link.href = form.action;
        }
      }

      if (type === 'product') {
        const imageWrap = createElement('span', 'codex-predictive-result__image');
        const featuredImage = item.featured_image;
        const imageUrl = typeof featuredImage === 'string' ? featuredImage : featuredImage?.url;
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.width = 112;
          image.height = 112;
          image.loading = 'lazy';
          imageWrap.append(image);
        }
        link.append(imageWrap);

        const copy = createElement('span', 'codex-predictive-result__copy');
        copy.append(createElement('strong', '', item.title || 'View bag'));
        if (item.price) copy.append(createElement('small', '', formatPrice(item.price)));
        link.append(copy);
      } else {
        const label = type === 'query'
          ? (item.text || item.title || query)
          : (item.title || item.text || 'View result');
        link.append(createElement('span', '', label));
        link.append(createElement('span', 'codex-predictive-result__arrow', '→'));
      }

      return link;
    };

    const appendGroup = (title, items, type, query, limit) => {
      const visibleItems = items.slice(0, limit);
      if (!visibleItems.length) return 0;

      const group = createElement('section', `codex-predictive-results__group codex-predictive-results__group--${type}`);
      group.append(createElement('h3', 'codex-predictive-results__heading', title));
      const list = createElement('div', `codex-predictive-results__list codex-predictive-results__list--${type}`);
      visibleItems.forEach((item) => list.append(resultLink(item, type, query)));
      group.append(list);
      resultsContainer.append(group);
      return visibleItems.length;
    };

    const renderResults = (payload, query) => {
      const resources = payload?.resources?.results || {};
      const products = resources.products || [];
      const collections = resources.collections || [];
      const pages = resources.pages || [];
      const queries = resources.queries || [];

      resultsContainer.replaceChildren();
      const productLimit = window.matchMedia('(max-width: 767px)').matches ? 3 : 4;
      let count = appendGroup('Bags', products, 'product', query, productLimit);
      count += appendGroup('Suggested searches', queries, 'query', query, 3);
      count += appendGroup('Collections', collections, 'collection', query, 3);
      count += appendGroup('Helpful pages', pages, 'page', query, 3);

      defaultContent.hidden = true;
      emptyContent.hidden = count > 0;
      resultsContainer.hidden = count === 0;
      allResults.hidden = false;
      queryLabel.textContent = query;
      input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
      setStatus(count ? `${count} search suggestions available.` : 'No exact matches. Popular categories are shown.');
    };

    const requestResults = async (query) => {
      activeRequest?.abort();
      activeRequest = new AbortController();
      overlay.classList.add('codex-search-overlay--loading');
      setStatus('Loading search suggestions.');

      const endpoint = new URL(predictiveUrl.endsWith('.json') ? predictiveUrl : `${predictiveUrl}.json`, window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'product,collection,query');
      endpoint.searchParams.set('resources[limit]', '8');
      endpoint.searchParams.set('resources[limit_scope]', 'each');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'hide');
      endpoint.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag');

      try {
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
          signal: activeRequest.signal
        });
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
        renderResults(await response.json(), query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        resultsContainer.hidden = true;
        emptyContent.hidden = false;
        defaultContent.hidden = true;
        allResults.hidden = false;
        queryLabel.textContent = query;
        input.setAttribute('aria-expanded', 'false');
        setStatus('Suggestions are unavailable. Press Enter to see all search results.');
      } finally {
        overlay.classList.remove('codex-search-overlay--loading');
      }
    };

    const handleInput = () => {
      const query = input.value.trim();
      clearButton.hidden = !query;
      window.clearTimeout(debounceTimer);

      if (query.length < 2) {
        resetResults();
        clearButton.hidden = !query;
        if (query.length === 1) setStatus('Type one more character for suggestions.');
        return;
      }

      debounceTimer = window.setTimeout(() => requestResults(query), 250);
    };

    allResults.addEventListener('click', () => form.requestSubmit());

    const openSearch = (trigger) => {
      returnFocusTo = trigger;
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('codex-search-open');
      document.body.classList.remove('reveal-mobile-nav', 'reveal-mobile-nav--revealed');
      window.requestAnimationFrame(() => {
        overlay.classList.add('codex-search-overlay--open');
        input.focus();
      });
    };

    const closeSearch = () => {
      activeRequest?.abort();
      overlay.classList.remove('codex-search-overlay--open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('codex-search-open');
      window.setTimeout(() => {
        overlay.hidden = true;
        returnFocusTo?.focus();
      }, 180);
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.codex-search-toggle');
      if (trigger) {
        event.preventDefault();
        openSearch(trigger);
        return;
      }

      if (event.target.closest('[data-codex-search-close]') && event.target.closest('[data-codex-search-overlay]') === overlay) {
        event.preventDefault();
        closeSearch();
      }
    });

    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        return;
      }

      if (event.key === 'ArrowDown' && event.target === input) {
        const firstResult = resultsContainer.querySelector('[role="option"]');
        if (firstResult) {
          event.preventDefault();
          firstResult.focus();
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...overlay.querySelectorAll('a[href], button:not([disabled]):not([hidden]), input:not([disabled])')]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    input.addEventListener('input', handleInput);
    form.addEventListener('reset', () => {
      window.setTimeout(() => {
        clearButton.hidden = true;
        resetResults();
        input.focus();
      }, 0);
    });
  };

  const initStandaloneSearch = (root = document) => {
    root.querySelectorAll?.('.codex-search-page .main-search').forEach((search) => {
      if (search.dataset.codexBound === 'true') return;
      search.dataset.codexBound = 'true';

      const form = search.querySelector('.main-search__form');
      const input = search.querySelector('.main-search__input');
      if (!form || !input) return;

      const syncState = () => search.classList.toggle('main-search--terms-entered', Boolean(input.value.trim()));
      input.addEventListener('input', syncState);
      form.addEventListener('reset', () => {
        window.setTimeout(() => {
          syncState();
          input.focus();
        }, 0);
      });
      syncState();
    });
  };

  bindHeaderMenus();
  initPredictiveSearch();
  initStandaloneSearch();
  initDirectionAwareHeader();

  document.addEventListener('shopify:section:load', (event) => {
    bindHeaderMenus(event.target);
    initPredictiveSearch(event.target);
    initStandaloneSearch(event.target);
    if (event.target.matches?.('.section-header') || event.target.querySelector?.('.section-header')) {
      initDirectionAwareHeader();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('.section-header') || event.target.querySelector?.('.section-header')) {
      headerScrollController?.destroy();
      headerScrollController = undefined;
    }
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest(menuSelector)) {
      document.querySelectorAll(menuSelector).forEach((details) => {
        details.open = false;
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || document.body.classList.contains('codex-search-open')) return;
    const openMenu = document.querySelector(`${menuSelector}[open]`);
    if (!openMenu) return;
    openMenu.open = false;
    openMenu.querySelector('summary')?.focus();
  });
})();
